import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Play, Pause, X, ChevronRight, Volume2, VolumeX, Radio, Music2, Sparkles, Clock, Cast, Share2, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchSnapshot,
  fetchHistoricalMedia,
  postNarrate,
  postPrepareNextEpisode,
  fetchConfig,
} from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { pickAmbientTrack } from "@/lib/ambient-tracks";
import { useEntitlement } from "@/context/EntitlementContext";
import PricingModal from "@/components/PricingModal";
import TVCastOverlay from "@/components/TVCastOverlay";

/**
 * CinemaMode — Netflix-style lean-back "watch a day in history".
 *
 * Phase 2 additions
 * -----------------
 * • "Up Next" overlay on the final chapter with 10s countdown → auto-advance
 * • Playlist mode toggle: "Chronological" (next calendar day) vs
 *   "Era Binge" (next iconic date in the same decade)
 * • Cancel button aborts the countdown
 *
 * Phase 3 additions
 * -----------------
 * • Cinema Mode narration is metered — 2 free plays/day for anonymous & free
 *   users; unlimited for TimeCapsule Premium. Backend 402 triggers PricingModal.
 * • "Cancel Auto-Play" also stays on the outro chapter so the user can browse.
 */
export default function CinemaMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { entitlement, refresh: refreshEntitlement } = useEntitlement();

  const [snapshot, setSnapshot] = useState(null);
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);

  const [chapterIdx, setChapterIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioElapsed, setAudioElapsed] = useState(0);
  const [narrationReady, setNarrationReady] = useState(false);
  const [muted, setMuted] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingReason, setPricingReason] = useState("manual");

  // ---- Phase 2 state ----
  // Persist the user's playlist mode across episodes.
  const [playlistMode, setPlaylistMode] = useState(() => {
    try { return localStorage.getItem("tc_playlist_mode") || "chronological"; } catch { return "chronological"; }
  });
  const setPlaylistModeAndPersist = (m) => {
    setPlaylistMode(m);
    try { localStorage.setItem("tc_playlist_mode", m); } catch {}
  };
  const [nextEpisode, setNextEpisode] = useState(null); // {id,date,region,label,mode}
  const [nextEpisodeLoading, setNextEpisodeLoading] = useState(false);
  const [nextCountdown, setNextCountdown] = useState(null); // seconds remaining, null=off
  const [autoAdvanceCancelled, setAutoAdvanceCancelled] = useState(false);

  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const idleTimerRef = useRef(null);
  const prewarmRef = useRef(false);
  const audioPromiseRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const [musicOn, setMusicOn] = useState(true);
  const [musicStarted, setMusicStarted] = useState(false);
  const [narratorMeta, setNarratorMeta] = useState(null); // {creator_voice, creator_name}

  // ---- Phase 4 (watch-easier) state ----
  const [showTVCast, setShowTVCast] = useState(false);
  // Mobile fullscreen nudge: show once per device on the first Cinema Mode load
  const [showFullscreenNudge, setShowFullscreenNudge] = useState(false);

  // Load snapshot + media in parallel; refresh entitlement so paywall gate
  // uses fresh quota (important between episodes / after payment).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setChapterIdx(0);
    setAudioSrc(null);
    setNarrationReady(false);
    setNextEpisode(null);
    setAutoAdvanceCancelled(false);
    setNextCountdown(null);
    setShowPricing(false);
    prewarmRef.current = false;
    refreshEntitlement().catch(() => {});
    (async () => {
      try {
        const [s, m] = await Promise.all([
          fetchSnapshot(id),
          fetchHistoricalMedia(id).catch(() => ({ images: [] })),
        ]);
        if (cancelled) return;
        setSnapshot(s);
        setMedia(m);
      } catch (e) {
        if (!cancelled) toast.error("Could not load episode");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, refreshEntitlement]);

  // Compose "chapters" — narrative beats the viewer moves through
  const chapters = useMemo(() => {
    if (!snapshot) return [];
    const s = snapshot.snapshot || {};
    const meta = s._meta || {};
    const news = s.news || [];
    const weather = s.weather || {};
    const musicBlock = s.music || {};
    const topSongs = Array.isArray(musicBlock) ? musicBlock : (musicBlock.top_songs || []);
    const cinemaBlock = s.cinema || s.movies || {};
    const movies = Array.isArray(cinemaBlock) ? cinemaBlock : (cinemaBlock.top_movies || []);
    const c = [];

    c.push({
      kind: "title",
      overline: meta.long_date || snapshot.date,
      title: `${meta.region?.flag || ""} ${meta.region?.name || snapshot.region}`.trim(),
      body: "A day in history",
    });

    news.slice(0, 3).forEach((n, i) => {
      c.push({
        kind: "news",
        overline: `Headline ${i + 1}`,
        title: n.headline || "",
        body: n.body || n.summary || "",
      });
    });

    if (weather && weather.condition) {
      const hi = weather.high_c ?? weather.high_f ?? null;
      const unit = weather.high_c != null ? "°C" : (weather.high_f != null ? "°F" : "");
      c.push({
        kind: "weather",
        overline: "The weather",
        title: hi != null ? `${weather.condition} · ${hi}${unit}` : weather.condition,
        body: weather.notes || weather.summary || (weather.city ? `Reported from ${weather.city}.` : ""),
      });
    }

    if (topSongs.length > 0) {
      const top = topSongs.slice(0, 3).map((t) => `${t.rank || ""} ${t.title} — ${t.artist}`.trim()).join("   ·   ");
      c.push({
        kind: "music",
        overline: "On the radio",
        title: topSongs[0].title || "",
        body: top,
      });
    }

    const topMovie = movies[0];
    if (topMovie) {
      c.push({
        kind: "cinema",
        overline: "At the picture-house",
        title: topMovie.title || "",
        body: topMovie.note || topMovie.blurb || topMovie.summary || "",
      });
    }

    c.push({
      kind: "outro",
      overline: `${meta.region?.name || snapshot.region} · ${snapshot.date}`,
      title: "End of episode",
      body: "Up next…",
    });

    return c;
  }, [snapshot]);

  const ambient = useMemo(() => {
    if (!snapshot) return null;
    const year = (snapshot.date || "").slice(0, 4);
    return pickAmbientTrack(year);
  }, [snapshot]);

  // Volume ducking
  const [narrationActive, setNarrationActive] = useState(false);
  useEffect(() => {
    if (!musicRef.current) return;
    const baseVolume = musicOn && !muted ? 0.18 : 0;
    const duckedVolume = narrationActive ? 0.03 : baseVolume;
    musicRef.current.volume = duckedVolume;
  }, [musicOn, muted, narrationActive, ambient]);

  // Chapter timing
  useEffect(() => {
    if (!playing || chapters.length === 0) return;
    if (musicRef.current && musicOn && musicRef.current.paused) {
      musicRef.current.play().catch(() => {});
    }
    const timer = setTimeout(() => {
      if (chapterIdx < chapters.length - 1) setChapterIdx((i) => i + 1);
      else setPlaying(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [chapterIdx, playing, chapters.length, musicOn]);

  // Warm the ambient music the instant the snapshot is loaded. The click that
  // brought the user to /watch/:id already counts as the user gesture, so
  // autoplay usually succeeds.
  useEffect(() => {
    if (!snapshot || !musicRef.current || !musicOn) return;
    try { musicRef.current.load(); } catch {}
    musicRef.current.play().catch(() => {
      // Autoplay blocked — will start on first user gesture via bumpChrome
    });
  }, [snapshot, musicOn]);

  // Pause ambient music when playback pauses / music toggled off
  useEffect(() => {
    if (!musicRef.current) return;
    if (!playing || !musicOn) {
      musicRef.current.pause();
    }
  }, [playing, musicOn]);

  // Prewarm narration — but only if we know we have quota (free) or premium.
  // If quota is already exhausted, this useEffect flips the paywall instead so
  // the 4th /watch load pops the upgrade modal immediately.
  useEffect(() => {
    if (!snapshot || prewarmRef.current) return;

    // Wait for entitlement to load before deciding anything.
    if (!entitlement) return;

    // No plays left and not premium → hard paywall on load.
    if (!entitlement.is_premium && entitlement.plays_remaining === 0) {
      prewarmRef.current = true;
      setPricingReason("quota_exhausted");
      setShowPricing(true);
      setPlaying(false);
      trackEvent("paywall_shown", { reason: "quota_exhausted_on_load" });
      return;
    }

    prewarmRef.current = true;
    // Start prewarming IMMEDIATELY (no 2s delay) so narration is ready by the
    // time the user hits chapter 2. Auto-plays the moment it lands.
    audioPromiseRef.current = postNarrate(id, "onyx", "cinema")
      .then((data) => {
        setNarrationReady(true);
        refreshEntitlement().catch(() => {});
        // Auto-play the narration inline — much faster than waiting for
        // the user to click. The user gesture that opened Cinema Mode still
        // counts, so autoplay is allowed.
        if (data?.audio_base64) {
          const src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`;
          setAudioSrc(src);
          // Small delay so React can attach the <audio> ref before we play
          setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.play().catch(() => {
                // Autoplay blocked — user will click Play Narration manually
              });
            }
          }, 100);
        }
        return data;
      })
      .catch((err) => {
        if (err?.response?.status === 402) {
          setPricingReason("quota_exhausted");
          setShowPricing(true);
          setPlaying(false);
          trackEvent("paywall_shown", { reason: "quota_exhausted_race" });
          refreshEntitlement().catch(() => {});
        }
      });
  }, [snapshot, id, entitlement, refreshEntitlement]);

  // ---- Phase 2: fetch next episode when snapshot loads ----
  useEffect(() => {
    if (!snapshot) return;
    let cancelled = false;
    setNextEpisodeLoading(true);
    // Prepare a lightweight preview by calling next-episode (no snapshot creation)
    // We use the /prepare endpoint only when the user actually decides to auto-advance.
    // For the preview, use fetchNextEpisode? No — we want the ID ready so the user
    // can click "Play next" instantly. Prepare eagerly *only* if snapshot is fresh.
    (async () => {
      try {
        const data = await postPrepareNextEpisode(id, playlistMode);
        if (cancelled) return;
        setNextEpisode(data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("next-episode prepare failed", e?.response?.data || e);
      } finally {
        if (!cancelled) setNextEpisodeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [snapshot, id, playlistMode]);

  // ---- Phase 2: "Up Next" 10s countdown when the last chapter ends ----
  const isFinalChapter = chapterIdx === chapters.length - 1 && chapters.length > 0;
  useEffect(() => {
    if (!isFinalChapter || autoAdvanceCancelled || !nextEpisode?.id) {
      setNextCountdown(null);
      clearInterval(countdownTimerRef.current);
      return;
    }
    setNextCountdown(10);
    clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = setInterval(() => {
      setNextCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          clearInterval(countdownTimerRef.current);
          // Advance
          trackEvent("cinema_auto_advance", { mode: playlistMode, to: nextEpisode.id });
          navigate(`/watch/${nextEpisode.id}`);
          return null;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(countdownTimerRef.current);
  }, [isFinalChapter, autoAdvanceCancelled, nextEpisode, playlistMode, navigate]);

  // Auto-hide chrome + kick off music on first user gesture
  const bumpChrome = () => {
    setChromeVisible(true);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setChromeVisible(false), 4000);
    if (musicRef.current && musicOn && musicRef.current.paused) {
      musicRef.current.play().catch(() => {});
    }
  };
  useEffect(() => {
    bumpChrome();
    return () => {
      clearTimeout(idleTimerRef.current);
      clearInterval(elapsedTimerRef.current);
      clearInterval(countdownTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch narrator config once on mount
  useEffect(() => {
    fetchConfig()
      .then((cfg) => setNarratorMeta(cfg?.narration || null))
      .catch(() => {});
  }, []);

  // Mobile: on first Cinema Mode ever, offer to go fullscreen (feels app-like).
  useEffect(() => {
    if (!snapshot) return;
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
    if (!isMobile) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem("tc_fs_nudge_dismissed") === "1"; } catch {}
    if (dismissed) return;
    // Not already in fullscreen?
    if (document.fullscreenElement) return;
    const t = setTimeout(() => setShowFullscreenNudge(true), 800);
    return () => clearTimeout(t);
  }, [snapshot]);

  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      trackEvent("cinema_fullscreen_enter");
    } catch { /* ignore */ }
    setShowFullscreenNudge(false);
    try { localStorage.setItem("tc_fs_nudge_dismissed", "1"); } catch {}
  };
  const dismissFullscreenNudge = () => {
    setShowFullscreenNudge(false);
    try { localStorage.setItem("tc_fs_nudge_dismissed", "1"); } catch {}
  };

  const shareLink = async () => {
    const url = `${window.location.origin}/watch/${id}`;
    const title = `Watch ${snapshot?.snapshot?._meta?.long_date || snapshot?.date} on TimeCapsule 24`;
    trackEvent("cinema_share_click", { snapshot_id: id });
    // Native share on mobile / supported browsers
    if (navigator.share) {
      try {
        await navigator.share({ url, title, text: title });
        return;
      } catch { /* user cancelled */ }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch {
      toast.error("Could not copy link — try again");
    }
  };

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      bumpChrome();
      if (e.key === "Escape") {
        // If countdown is running, first Esc cancels it; second Esc exits.
        if (nextCountdown !== null) {
          setAutoAdvanceCancelled(true);
          return;
        }
        navigate(`/snapshot/${id}`);
      }
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      if (e.key === "ArrowRight") setChapterIdx((i) => Math.min(chapters.length - 1, i + 1));
      if (e.key === "ArrowLeft") setChapterIdx((i) => Math.max(0, i - 1));
      if (e.key === "m") setMuted((m) => !m);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, chapters.length, nextCountdown]);

  const playNarration = async () => {
    if (audioSrc && audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play().catch(() => {});
      else audioRef.current.pause();
      return;
    }
    setAudioLoading(true);
    setAudioElapsed(0);
    trackEvent("cinema_narration_play", { snapshot_id: id });

    const started = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setAudioElapsed(Math.floor((Date.now() - started) / 1000));
    }, 500);

    try {
      let data = audioPromiseRef.current ? await audioPromiseRef.current : null;
      if (!data || !data.audio_base64) {
        data = await postNarrate(id, "onyx", "cinema");
      }
      if (!data || !data.audio_base64) throw new Error("empty");
      const src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`;
      setAudioSrc(src);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 50);
      // Successful play — bump entitlement counter in UI
      refreshEntitlement().catch(() => {});
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        setPricingReason("quota_exhausted");
        setShowPricing(true);
        trackEvent("paywall_shown", { reason: "quota_exhausted" });
        refreshEntitlement().catch(() => {});
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Could not load narration");
      }
    } finally {
      clearInterval(elapsedTimerRef.current);
      setAudioLoading(false);
    }
  };

  const cancelAutoAdvance = () => {
    setAutoAdvanceCancelled(true);
    setNextCountdown(null);
    clearInterval(countdownTimerRef.current);
  };

  const playNextNow = () => {
    if (!nextEpisode?.id) return;
    clearInterval(countdownTimerRef.current);
    trackEvent("cinema_next_click", { mode: playlistMode, to: nextEpisode.id });
    navigate(`/watch/${nextEpisode.id}`);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-paper" data-testid="cinema-loading">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-3" />
          <div className="font-mono uppercase tracking-widest text-xs opacity-70">Cueing the reels…</div>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="fixed inset-0 bg-black text-paper flex items-center justify-center">
        <button onClick={() => navigate("/")} className="border-2 border-paper px-4 py-2 font-mono uppercase text-xs">
          Return to lobby
        </button>
      </div>
    );
  }

  const chapter = chapters[chapterIdx] || {};
  const images = media?.images || [];
  const bgImage = images.length > 0 ? images[chapterIdx % images.length] : null;
  const showUpNextOverlay = isFinalChapter && !autoAdvanceCancelled && nextEpisode?.id;
  const plansRemaining = entitlement?.plays_remaining;
  const showQuotaBadge = entitlement && !entitlement.is_premium && typeof plansRemaining === "number";
  const showLastPlayNudge = entitlement && !entitlement.is_premium && plansRemaining === 1;

  return (
    <div
      className="fixed inset-0 bg-black text-paper overflow-hidden"
      onMouseMove={bumpChrome}
      onClick={bumpChrome}
      data-testid="cinema-mode"
    >
      {/* BACKDROP */}
      <div className="absolute inset-0">
        {bgImage ? (
          <img
            key={bgImage.full_url + chapterIdx}
            src={bgImage.thumb_url}
            alt=""
            referrerPolicy="no-referrer"
            className="absolute inset-0 w-full h-full object-cover tc-kenburns"
            data-testid={`cinema-bg-${chapterIdx}`}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-ink to-black" />
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/85 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40 pointer-events-none" />

      {/* CHAPTER TEXT OVERLAY */}
      <div className="absolute inset-0 flex items-end sm:items-center p-6 sm:p-16 lg:p-24 pointer-events-none">
        <div className="max-w-5xl">
          <div key={chapterIdx} className="tc-cinema-in">
            <div className="font-mono text-xs sm:text-base uppercase tracking-[0.35em] text-press-red mb-3 sm:mb-5">
              — {chapter.overline} —
            </div>
            <div className="font-headline font-black leading-[0.92] tracking-tighter text-4xl sm:text-6xl lg:text-7xl xl:text-8xl uppercase">
              {chapter.title}
            </div>
            {chapter.body && (
              <div className="mt-5 sm:mt-8 font-body text-lg sm:text-2xl lg:text-3xl leading-relaxed max-w-3xl text-paper/85">
                {chapter.body}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* "Last free play" nudge banner — Phase 3 conversion funnel */}
      {showLastPlayNudge && chromeVisible && (
        <div
          className="absolute top-0 inset-x-0 z-20 bg-press-red text-paper font-mono uppercase tracking-widest text-[10px] sm:text-xs px-4 py-2 flex items-center justify-center gap-3 tc-cinema-in"
          data-testid="last-play-nudge"
        >
          <span className="animate-pulse">◉ Last free episode today</span>
          <span className="opacity-70 hidden sm:inline">— unlimited plays with Premium</span>
          <button
            onClick={() => { setPricingReason("manual"); setShowPricing(true); trackEvent("upgrade_click", { source: "last_play_nudge" }); }}
            className="ml-2 border border-paper px-2.5 py-0.5 hover:bg-paper hover:text-press-red"
            data-testid="last-play-nudge-cta"
          >
            Upgrade →
          </button>
        </div>
      )}

      {/* Top chrome */}
      <div
        className={`absolute top-0 inset-x-0 ${showLastPlayNudge && chromeVisible ? "pt-10" : ""} p-4 sm:p-6 flex items-center justify-between gap-3 transition-opacity duration-500 ${chromeVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-paper/70">
          Episode · {snapshot.snapshot?._meta?.long_date || snapshot.date}
        </div>

        {/* Playlist mode toggle */}
        <div
          className="hidden sm:inline-flex items-center border-2 border-paper/40 overflow-hidden"
          data-testid="playlist-mode-toggle"
        >
          <button
            onClick={() => setPlaylistModeAndPersist("chronological")}
            className={`px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest flex items-center gap-1.5 ${playlistMode === "chronological" ? "bg-paper text-ink" : "text-paper/70 hover:text-paper"}`}
            data-testid="playlist-mode-chronological"
          >
            <Clock size={12} /> Chronological
          </button>
          <button
            onClick={() => setPlaylistModeAndPersist("era")}
            className={`px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest flex items-center gap-1.5 border-l-2 border-paper/40 ${playlistMode === "era" ? "bg-press-red text-paper" : "text-paper/70 hover:text-paper"}`}
            data-testid="playlist-mode-era"
          >
            <Sparkles size={12} /> Era Binge
          </button>
        </div>

        <div className="flex items-center gap-2">
          {showQuotaBadge && (
            <button
              onClick={() => { setPricingReason("manual"); setShowPricing(true); }}
              className={`hidden md:inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] px-2.5 py-1.5 border ${plansRemaining <= 1 ? "border-press-red text-press-red animate-pulse" : "border-paper/40 text-paper/70"} hover:bg-paper hover:text-ink`}
              data-testid="quota-badge"
              title="Cinema plays remaining today"
            >
              {plansRemaining} / {entitlement.plays_limit} free plays
            </button>
          )}
          {entitlement?.is_premium && (
            <span className="hidden md:inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] px-2.5 py-1.5 border border-press-red text-press-red">
              ✦ Premium
            </span>
          )}
          <button
            onClick={() => { setShowTVCast(true); trackEvent("cinema_cast_click"); }}
            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] sm:text-xs border-2 border-paper/60 px-2.5 py-2 hover:bg-paper hover:text-ink"
            title="Watch on TV / another device"
            aria-label="Cast to another screen"
            data-testid="cinema-cast-btn"
          >
            <Cast size={14} /> <span className="hidden sm:inline">Cast</span>
          </button>
          <button
            onClick={shareLink}
            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] sm:text-xs border-2 border-paper/60 px-2.5 py-2 hover:bg-paper hover:text-ink"
            title="Share this episode"
            aria-label="Share this episode"
            data-testid="cinema-share-btn"
          >
            <Share2 size={14} /> <span className="hidden sm:inline">Share</span>
          </button>
          <button
            onClick={() => navigate(`/snapshot/${id}`)}
            className="inline-flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs border-2 border-paper/60 px-3 py-2 hover:bg-paper hover:text-ink"
            data-testid="cinema-close-btn"
          >
            <X size={14} /> Exit
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className={`absolute bottom-24 sm:bottom-28 inset-x-0 px-6 sm:px-16 transition-opacity duration-500 ${chromeVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div className="flex gap-1">
          {chapters.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-paper/25 overflow-hidden">
              <div
                className={`h-full bg-press-red transition-all ${i < chapterIdx ? "w-full" : i === chapterIdx ? "w-full tc-progress-fill" : "w-0"}`}
                style={{ animationPlayState: playing && i === chapterIdx ? "running" : "paused" }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom chrome — controls */}
      <div
        className={`absolute bottom-0 inset-x-0 p-4 sm:p-6 flex items-center gap-3 flex-wrap transition-opacity duration-500 ${chromeVisible ? "opacity-100" : "opacity-0"}`}
      >
        <button
          data-testid="cinema-play-btn"
          onClick={() => setPlaying((p) => !p)}
          className="inline-flex items-center gap-2 bg-paper text-ink border-2 border-paper px-4 py-3 font-mono uppercase text-xs tracking-widest hover:bg-press-red hover:text-paper hover:border-press-red"
        >
          {playing ? <Pause size={14} /> : <Play size={14} />}
          {playing ? "Pause" : "Play"}
        </button>

        <button
          data-testid="cinema-narration-btn"
          onClick={playNarration}
          disabled={audioLoading}
          title={narrationReady ? "Narration ready — click to pause/resume" : "Preparing narration…"}
          className="inline-flex items-center gap-2 border-2 border-paper/70 px-4 py-3 font-mono uppercase text-xs tracking-widest hover:bg-paper hover:text-ink disabled:opacity-60"
        >
          {audioLoading || (!narrationReady && !audioSrc) ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
          {audioLoading
            ? (audioElapsed > 0 ? `Preparing · ${audioElapsed}s` : "Preparing…")
            : audioSrc
              ? (audioRef.current?.paused ? "Resume voice" : "Pause voice")
              : (narrationReady ? "Play narration ▸" : "Warming voice…")}
        </button>

        <button
          onClick={() => {
            setMusicOn((m) => !m);
            if (musicRef.current && !musicOn) {
              musicRef.current.play().catch(() => {});
            }
          }}
          className={`inline-flex items-center gap-2 border-2 px-3 py-3 font-mono uppercase text-xs tracking-widest hover:bg-paper hover:text-ink ${musicOn ? "border-press-red text-press-red" : "border-paper/70"}`}
          aria-label="Toggle ambient music"
          title={musicOn ? "Ambient music on — click to mute" : "Ambient music off — click to enable"}
          data-testid="cinema-music-btn"
        >
          <Music2 size={14} />
          <span className="hidden md:inline">{musicOn ? "Music" : "No music"}</span>
        </button>

        <button
          onClick={() => setMuted((m) => !m)}
          className="inline-flex items-center gap-2 border-2 border-paper/70 px-3 py-3 font-mono uppercase text-xs tracking-widest hover:bg-paper hover:text-ink"
          aria-label="Toggle mute"
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>

        <div className="ml-auto font-mono text-[10px] sm:text-xs uppercase tracking-widest text-paper/60 hidden lg:block">
          Chapter {chapterIdx + 1} / {chapters.length}   ·   ← → skip · space pause · esc exit
        </div>

        {isFinalChapter && !showUpNextOverlay && (
          <button
            data-testid="cinema-next-episode"
            onClick={() => { setChapterIdx(0); setPlaying(true); setAutoAdvanceCancelled(false); }}
            className="inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-4 py-3 font-mono uppercase text-xs tracking-widest tc-media-pulse"
          >
            Replay episode <ChevronRight size={14} />
          </button>
        )}
      </div>

      {/* --- UP NEXT overlay (Phase 2) --- */}
      {showUpNextOverlay && (
        <div
          className="absolute right-4 bottom-24 sm:right-8 sm:bottom-32 w-[min(92vw,420px)] bg-ink/92 backdrop-blur-md border-2 border-paper text-paper shadow-[8px_8px_0_rgba(186,63,29,0.4)] p-5 tc-cinema-in"
          data-testid="up-next-card"
        >
          <div className="flex items-center gap-2 font-mono uppercase tracking-[0.3em] text-[10px] text-press-red">
            <Sparkles size={12} /> Up Next · {playlistMode === "era" ? "Era Binge" : "Chronological"}
          </div>
          <div className="mt-2 font-headline uppercase font-black text-2xl sm:text-3xl leading-tight tracking-tight" data-testid="up-next-label">
            {nextEpisode?.label || "The next day"}
          </div>
          <div className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-paper/60">
            {nextEpisode?.date} · {nextEpisode?.region}
          </div>

          {nextCountdown !== null && (
            <div className="mt-3 h-1 bg-paper/20 overflow-hidden">
              <div
                className="h-full bg-press-red transition-all"
                style={{ width: `${(1 - nextCountdown / 10) * 100}%` }}
              />
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={playNextNow}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-3 py-2.5 font-mono uppercase text-[11px] tracking-widest hover:bg-paper hover:text-ink hover:border-paper"
              data-testid="up-next-play-btn"
            >
              <Play size={12} /> Play now {nextCountdown !== null ? `· ${nextCountdown}s` : ""}
            </button>
            <button
              onClick={cancelAutoAdvance}
              className="inline-flex items-center gap-2 border-2 border-paper/60 px-3 py-2.5 font-mono uppercase text-[11px] tracking-widest hover:bg-paper hover:text-ink"
              data-testid="up-next-cancel-btn"
            >
              Cancel
            </button>
          </div>

          {nextEpisodeLoading && (
            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-paper/50 flex items-center gap-1.5">
              <Loader2 size={10} className="animate-spin" /> Preparing next episode…
            </div>
          )}
        </div>
      )}

      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          muted={muted}
          className="hidden"
          onPlay={() => setNarrationActive(true)}
          onPause={() => setNarrationActive(false)}
          onEnded={() => setNarrationActive(false)}
        />
      )}

      {ambient && (
        <audio
          ref={(el) => {
            musicRef.current = el;
            if (el) el.volume = musicOn && !muted ? 0.18 : 0;
          }}
          src={ambient.file}
          loop
          preload="auto"
          className="hidden"
          onPlaying={() => setMusicStarted(true)}
        />
      )}

      {chromeVisible && (musicStarted || narratorMeta?.creator_voice) && (
        <div
          className="absolute bottom-24 left-4 sm:left-6 font-mono text-[9px] uppercase tracking-widest text-paper/40 pointer-events-none transition-opacity duration-500 space-y-0.5"
          data-testid="cinema-attribution"
        >
          {ambient && musicStarted && (
            <div data-testid="music-attribution">
              ♪ {ambient.title} · {ambient.attribution}
            </div>
          )}
          {narratorMeta?.creator_voice && narratorMeta?.creator_name && (
            <div data-testid="narrator-attribution" className="text-press-red/70">
              ✦ Narrated by {narratorMeta.creator_name}
            </div>
          )}
        </div>
      )}

      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        reason={pricingReason}
        playsRemaining={entitlement?.plays_remaining ?? 0}
      />

      <TVCastOverlay
        open={showTVCast}
        onClose={() => setShowTVCast(false)}
        url={typeof window !== "undefined" ? `${window.location.origin}/watch/${id}` : ""}
        snapshotId={id}
      />

      {/* Mobile fullscreen nudge — one-time per device */}
      {showFullscreenNudge && (
        <div
          className="fixed bottom-4 inset-x-4 z-[8000] bg-paper text-ink border-2 border-ink shadow-[6px_6px_0_#1A1C20] p-4 flex items-center gap-3 tc-cinema-in"
          data-testid="fullscreen-nudge"
        >
          <Maximize2 size={20} className="text-press-red flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-headline uppercase font-bold text-sm leading-tight">Go fullscreen for the full effect</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">Recommended for TV / iPad viewing</div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={enterFullscreen}
              className="bg-press-red text-paper border-2 border-press-red px-3 py-2 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:border-ink"
              data-testid="fullscreen-nudge-accept"
            >
              Yes
            </button>
            <button
              onClick={dismissFullscreenNudge}
              className="border-2 border-ink px-3 py-2 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-paper"
              data-testid="fullscreen-nudge-dismiss"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
